/**
 * src/api/realtimeClient.js
 *
 * Thin wrapper around Azure Web PubSub for receiving state-change signals.
 *
 * Signals are one-way: the server publishes { type: 'state-changed', changedKeys? }
 * to the tenant group; this client receives and calls onMessage. No tenant data
 * is ever transmitted — signals only.
 *
 * Falls back silently to polling when:
 *  - The negotiate endpoint returns 503 (service not configured)
 *  - The WebSocket connection cannot be established
 *  - Any network error occurs
 *
 * Reconnect uses exponential back-off capped at 30 s. The caller must call
 * disconnect() in the component cleanup to avoid memory leaks.
 */

import { getFunctionUrl } from './functionUrl.js';
import { getSessionToken } from './azureClient.js';

const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/**
 * Create a realtime client that subscribes to state-change signals for a tenant.
 *
 * The session JWT is read live from azureClient at each negotiate, so
 * reconnects after the 22 h sliding-session refresh use the rotated token
 * instead of a stale capture.
 *
 * @param {object} options
 * @param {string}   options.tenantId  The tenant id (used to derive the group).
 * @param {Function} options.onMessage Called with the parsed signal object.
 * @param {Function} [options.onStatus] Called with 'connecting' | 'connected' |
 *   'unavailable' | 'disconnected' as the connection changes, so a caller can
 *   run its own fallback refresh whenever signals are not being delivered.
 * @returns {{ disconnect: Function }}
 */
export function createRealtimeClient({ tenantId, onMessage, onStatus }) {
  let ws = null;
  let stopped = false;
  let attempt = 0;
  let reconnectTimer = null;

  function report(status) {
    if (stopped || !onStatus) return;
    try {
      onStatus(status);
    } catch {
      // A status listener must never break the connection logic.
    }
  }

  async function negotiate() {
    const url = getFunctionUrl('realtime-negotiate');
    const token = getSessionToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(url, { headers });
    if (res.status === 503 || res.status === 401 || res.status === 403) {
      // 503: service not configured. 401/403: no usable session for this
      // surface — retrying with the same credentials cannot succeed (a
      // re-login re-creates the client via usePersistence's effect).
      // Fall back to polling only, silently.
      return null;
    }
    if (!res.ok) throw new Error(`negotiate ${res.status}`);
    const { url: wsUrl } = await res.json();
    return wsUrl;
  }

  function connect() {
    if (stopped) return;

    negotiate()
      .then((wsUrl) => {
        if (stopped) return;
        if (!wsUrl) {
          report('unavailable');
          return;
        }

        ws = new WebSocket(wsUrl, 'json.webpubsub.azure.v1');

        ws.onopen = () => {
          attempt = 0; // reset back-off on successful connect
          report('connected');
        };

        ws.onmessage = (evt) => {
          try {
            const envelope = JSON.parse(evt.data);
            // Web PubSub wraps server messages as { type: 'message', data: '...' }
            if (envelope.type === 'message' && envelope.data != null) {
              const signal =
                typeof envelope.data === 'string' ? JSON.parse(envelope.data) : envelope.data;
              onMessage(signal);
            }
          } catch {
            // Malformed message — ignore.
          }
        };

        ws.onclose = () => {
          ws = null;
          if (!stopped) {
            report('disconnected');
            scheduleReconnect();
          }
        };

        ws.onerror = () => {
          // onclose fires after onerror, so reconnect is handled there.
          if (ws) ws.close();
        };
      })
      .catch(() => {
        if (!stopped) {
          report('disconnected');
          scheduleReconnect();
        }
      });
  }

  function scheduleReconnect() {
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
    attempt++;
    reconnectTimer = setTimeout(connect, delay);
  }

  report('connecting');
  connect();

  return {
    disconnect() {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (ws) {
        ws.onclose = null; // prevent reconnect loop on explicit close
        ws.close();
        ws = null;
      }
    },
  };
}
